import React, { useEffect, useState } from 'react';
import { Check, Eye, RotateCcw, Trash2 } from 'lucide-react';
import { apiDelete, apiGet, apiPost, apiPut } from '../utils/api';
import RatingDocumentModal from '../components/RatingDocumentModal';

type ListItem = {
  id: string;
  ratingType: 'TTM_CUP' | 'TTM_DOUBLES' | string;
  status: 'DRAFT' | 'POSTED' | string;
  weekKey: string;
  accrualDate?: string | null;
  itemsCount: number;
  totalPoints: number;
  createdAt: string;
};

type DocumentDetails = ListItem & {
  notes?: string | null;
  items: Array<{
    id: string;
    level: string;
    playerLevel?: string | null;
    position: number;
    totalPoints: number;
    tournamentsPlayed: number;
    grandSlamPoints: number;
    mastersPoints: number;
    finalsPoints: number;
    cup500Points: number;
    cup250Points: number;
    matchesPlayed: number;
    setDiff: number;
    gameDiff: number;
    player: { id: string; name: string };
    sources?: Array<{
      id: string;
      tournamentName: string;
      points: number;
      level: string;
      category?: string | null;
      stageCode?: string | null;
      stageName?: string | null;
      awardedAt?: string | null;
      expiresAt?: string | null;
    }>;
  }>;
};

const getStatusLabel = (status?: string) => {
  if (status === 'DRAFT') return 'Черновик';
  if (status === 'POSTED') return 'Проведен';
  return status || '-';
};

const getRatingTypeLabel = (type?: string) => {
  if (type === 'TTM_CUP') return 'TTM CUP';
  if (type === 'TTM_DOUBLES') return 'TTM DOUBLES';
  return type || '-';
};

const getTodayLocalDateInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const RatingDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState<'ALL' | 'TTM_CUP' | 'TTM_DOUBLES'>('ALL');
  const [selectedDocument, setSelectedDocument] = useState<DocumentDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [recalculationDirty, setRecalculationDirty] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [savingRecalculation, setSavingRecalculation] = useState(false);
  const [accrualDateSaving, setAccrualDateSaving] = useState(false);
  const [creatingType, setCreatingType] = useState<'TTM_CUP' | 'TTM_DOUBLES' | null>(null);
  const [createAccrualDate, setCreateAccrualDate] = useState<string>(getTodayLocalDateInput());

  const loadDocuments = async (searchValue?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', '100');
      if ((searchValue ?? search).trim()) {
        params.set('search', (searchValue ?? search).trim());
      }
      if (selectedType !== 'ALL') {
        params.set('ratingType', selectedType);
      }
      const data = await apiGet<{ documents: ListItem[] }>(`/rating-documents?${params.toString()}`);
      setDocuments(Array.isArray(data.documents) ? data.documents : []);
    } catch (error) {
      console.error('Ошибка загрузки журнала документов рейтинга:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [selectedType]);

  const handleOpenDocument = async (documentId: string) => {
    try {
      setDetailsLoading(true);
      const data = await apiGet<DocumentDetails>(`/rating-documents/${documentId}`);
      setSelectedDocument(data);
      setRecalculationDirty(false);
    } catch (error: any) {
      console.error('Ошибка открытия документа рейтинга:', error);
      alert(`Ошибка: ${error.message || 'Не удалось открыть документ'}`);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCreateWeekly = async (ratingType: 'TTM_CUP' | 'TTM_DOUBLES') => {
    if (!createAccrualDate) {
      alert('Укажите дату расчета для создания черновика.');
      return;
    }
    try {
      setCreatingType(ratingType);
      const result = await apiPost<{ created: boolean; document: ListItem }>(`/rating-documents/create-weekly-draft`, {
        ratingType,
        accrualDate: createAccrualDate
      });
      await loadDocuments();
      if (result?.document?.id) {
        await handleOpenDocument(result.document.id);
      }
    } catch (error: any) {
      console.error('Ошибка создания weekly-документа рейтинга:', error);
      alert(`Ошибка: ${error.message || 'Не удалось создать документ рейтинга'}`);
    } finally {
      setCreatingType(null);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    const target = documents.find((doc) => doc.id === documentId);
    if (target?.status !== 'DRAFT') {
      alert('Удаление доступно только для документов в статусе Черновик.');
      return;
    }
    const confirmed = window.confirm(`Удалить документ рейтинга "${getRatingTypeLabel(target?.ratingType)}" за неделю ${target?.weekKey}?`);
    if (!confirmed) return;

    try {
      setDeletingId(documentId);
      await apiDelete(`/rating-documents/${documentId}`);
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      if (selectedDocument?.id === documentId) setSelectedDocument(null);
    } catch (error: any) {
      console.error('Ошибка удаления документа рейтинга:', error);
      alert(`Ошибка: ${error.message || 'Не удалось удалить документ'}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRecalculatePreview = async () => {
    if (!selectedDocument?.id) return;
    try {
      setRecalculating(true);
      const preview = await apiPost<DocumentDetails>(`/rating-documents/${selectedDocument.id}/recalculate-preview`);
      setSelectedDocument(preview);
      setRecalculationDirty(true);
    } catch (error: any) {
      console.error('Ошибка пересчета документа рейтинга:', error);
      alert(`Ошибка: ${error.message || 'Не удалось выполнить пересчет'}`);
    } finally {
      setRecalculating(false);
    }
  };

  const handleSaveRecalculation = async () => {
    if (!selectedDocument?.id) return;
    try {
      setSavingRecalculation(true);
      const saved = await apiPost<DocumentDetails>(`/rating-documents/${selectedDocument.id}/recalculate-apply`);
      setSelectedDocument(saved);
      setRecalculationDirty(false);
      setDocuments((prev) =>
        prev.map((doc) => (doc.id === saved.id ? { ...doc, status: saved.status, itemsCount: saved.itemsCount, totalPoints: saved.totalPoints } : doc))
      );
    } catch (error: any) {
      console.error('Ошибка сохранения пересчета документа рейтинга:', error);
      alert(`Ошибка: ${error.message || 'Не удалось сохранить пересчет'}`);
    } finally {
      setSavingRecalculation(false);
    }
  };

  const handleChangeAccrualDate = async (nextDate: string) => {
    if (!selectedDocument?.id) return;
    try {
      setAccrualDateSaving(true);
      const updated = await apiPut<DocumentDetails>(`/rating-documents/${selectedDocument.id}/accrual-date`, { accrualDate: nextDate });
      setSelectedDocument((prev) => (prev ? { ...prev, accrualDate: updated.accrualDate } : prev));
      setDocuments((prev) => prev.map((doc) => (doc.id === updated.id ? { ...doc, accrualDate: updated.accrualDate || null } : doc)));
    } catch (error: any) {
      console.error('Ошибка обновления даты расчета рейтинга:', error);
      alert(`Ошибка: ${error.message || 'Не удалось обновить дату расчета'}`);
    } finally {
      setAccrualDateSaving(false);
    }
  };

  const handleChangeStatus = async (documentId: string, nextStatus: 'DRAFT' | 'POSTED') => {
    const target = documents.find((doc) => doc.id === documentId);
    const actionText = nextStatus === 'POSTED' ? 'провести' : 'отменить проведение (перевести в Черновик)';
    const confirmed = window.confirm(
      `Подтвердите: ${actionText} документ "${getRatingTypeLabel(target?.ratingType)}" за неделю ${target?.weekKey}?`
    );
    if (!confirmed) return;

    try {
      setStatusUpdatingId(documentId);
      const updated = await apiPut<ListItem>(`/rating-documents/${documentId}/status`, { status: nextStatus });
      setDocuments((prev) => prev.map((doc) => (doc.id === documentId ? { ...doc, status: updated.status } : doc)));
      if (selectedDocument?.id === documentId) {
        setSelectedDocument((prev) => (prev ? { ...prev, status: updated.status } : prev));
      }
    } catch (error: any) {
      console.error('Ошибка изменения статуса документа рейтинга:', error);
      alert(`Ошибка: ${error.message || 'Не удалось изменить статус документа'}`);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Журнал рейтинга</h1>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={createAccrualDate}
            onChange={(e) => setCreateAccrualDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            title="Дата расчета для нового черновика"
            aria-label="Дата расчета для нового черновика"
          />
          <button
            onClick={() => handleCreateWeekly('TTM_CUP')}
            disabled={creatingType !== null}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-60"
          >
            + Черновик TTM CUP
          </button>
          <button
            onClick={() => handleCreateWeekly('TTM_DOUBLES')}
            disabled={creatingType !== null}
            className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-60"
          >
            + Черновик TTM DOUBLES
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по неделе YYYY-MM-DD"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="ALL">Все типы</option>
            <option value="TTM_CUP">TTM CUP</option>
            <option value="TTM_DOUBLES">TTM DOUBLES</option>
          </select>
          <button onClick={() => loadDocuments(search)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            Найти
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Загрузка...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Дата</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Дата расчета</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Неделя</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Тип рейтинга</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Статус</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Строк</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Сумма очков</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Действия</th>
                </tr>
              </thead>
              <tbody>
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Документы рейтинга не найдены.
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id} className="border-t border-gray-100">
                      <td className="px-4 py-2">{new Date(doc.createdAt).toLocaleString('ru-RU')}</td>
                      <td className="px-4 py-2">{new Date(doc.accrualDate || doc.createdAt).toLocaleDateString('ru-RU')}</td>
                      <td className="px-4 py-2">{doc.weekKey}</td>
                      <td className="px-4 py-2">{getRatingTypeLabel(doc.ratingType)}</td>
                      <td className="px-4 py-2">{getStatusLabel(doc.status)}</td>
                      <td className="px-4 py-2 text-right">{doc.itemsCount}</td>
                      <td className="px-4 py-2 text-right font-medium">{doc.totalPoints}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleOpenDocument(doc.id)}
                            className="p-1.5 rounded text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            disabled={detailsLoading || deletingId === doc.id || statusUpdatingId === doc.id}
                            title="Просмотр"
                            aria-label="Просмотр"
                          >
                            <Eye size={16} />
                          </button>
                          {doc.status === 'DRAFT' ? (
                            <button
                              onClick={() => handleChangeStatus(doc.id, 'POSTED')}
                              className="p-1.5 rounded text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                              disabled={statusUpdatingId === doc.id || deletingId === doc.id}
                              title="Провести"
                              aria-label="Провести"
                            >
                              <Check size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleChangeStatus(doc.id, 'DRAFT')}
                              className="p-1.5 rounded text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              disabled={statusUpdatingId === doc.id || deletingId === doc.id}
                              title="Отменить (в Черновик)"
                              aria-label="Отменить (в Черновик)"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                          {doc.status === 'DRAFT' && (
                            <button
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="p-1.5 rounded text-red-700 hover:bg-red-100 disabled:opacity-50"
                              disabled={deletingId === doc.id || statusUpdatingId === doc.id}
                              title="Удалить"
                              aria-label="Удалить"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RatingDocumentModal
        open={!!selectedDocument}
        onClose={() => {
          setSelectedDocument(null);
          setRecalculationDirty(false);
        }}
        document={selectedDocument}
        onRecalculate={selectedDocument?.status === 'DRAFT' ? handleRecalculatePreview : undefined}
        onSaveRecalculation={handleSaveRecalculation}
        showSaveButton={recalculationDirty && selectedDocument?.status === 'DRAFT'}
        recalculateLoading={recalculating}
        saveLoading={savingRecalculation}
        onChangeAccrualDate={selectedDocument?.status === 'DRAFT' ? handleChangeAccrualDate : undefined}
        accrualDateSaving={accrualDateSaving}
      />
    </div>
  );
};

export default RatingDocuments;

